import { FormInput, FormSelect } from '@/shared/ui/components'
import { US_STATES, CANADIAN_PROVINCES } from '@/shared/config/states'
import { COUNTRIES } from '@/shared/config/countries'
import { countryHasStateOptions } from '../validate-form-values'
import type { JoinFormValues } from './form-values'

export type AddressSectionProps = {
  values: JoinFormValues
  errors: Record<string, string>
  isSubmitting: boolean
  onFieldChange: (field: keyof JoinFormValues, value: string) => void
}

export function AddressSection({
  values,
  errors,
  isSubmitting,
  onFieldChange,
}: AddressSectionProps) {
  const showStateField = countryHasStateOptions(values.country)

  return (
    <div className="flex flex-col gap-4">
      <FormInput
        id="addressLine1"
        label="Street address"
        required
        value={values.addressLine1 ?? ''}
        onChange={(v) => onFieldChange('addressLine1', v)}
        error={errors.addressLine1}
        disabled={isSubmitting}
      />

      <FormInput
        id="addressLine2"
        label="Apt, suite, unit (optional)"
        value={values.addressLine2 ?? ''}
        onChange={(v) => onFieldChange('addressLine2', v)}
        error={errors.addressLine2}
        disabled={isSubmitting}
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormInput
          id="city"
          label="City"
          required
          value={values.city ?? ''}
          onChange={(v) => onFieldChange('city', v)}
          error={errors.city}
          disabled={isSubmitting}
        />
        {showStateField ? (
          <FormSelect
            id="state"
            label="State"
            required
            value={values.state ?? ''}
            onChange={(v) => onFieldChange('state', v)}
            error={errors.state}
            disabled={isSubmitting}
            placeholder="Select state"
            options={
              values.country === 'CA'
                ? CANADIAN_PROVINCES.map((s) => ({ value: s.code, label: s.name }))
                : US_STATES.map((s) => ({ value: s.code, label: s.name }))
            }
          />
        ) : null}
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormInput
          id="zipCode"
          label="ZIP code"
          required
          value={values.zipCode ?? ''}
          onChange={(v) => onFieldChange('zipCode', v)}
          error={errors.zipCode}
          disabled={isSubmitting}
          placeholder="XXXXX or XXXXX-XXXX"
        />
        <FormSelect
          id="country"
          label="Country"
          required
          value={values.country ?? ''}
          onChange={(v) => onFieldChange('country', v)}
          error={errors.country}
          disabled={isSubmitting}
          placeholder="Select country"
          options={COUNTRIES.map((c) => ({ value: c.code, label: c.name }))}
        />
      </div>
    </div>
  )
}
